import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import Header from '../../components/dashboard-layout/Header';
import Footer from '../../components/dashboard-layout/Footer';
import CourseCard from '../../components/cards/CourseCard';
import { CourseSkeleton } from '../../components/skeletons/CourseSkeleton';
import Thumbnail from '/thumbnail.png';
import { DataNotFoundSection } from '../../components/sections/DataNotFoundSection';
import { Input } from '../../components/ui/input';
import { Filter, SlidersHorizontal, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';

const CoursesCoverPage = () => {
    const {
        data: coursesData,
        isLoading,
        isError,
    } = useQuery({
        queryKey: queryKeys.coursesList({}),
        queryFn: () => api.getCoursesList(),
    });

    const courses = (coursesData?.courses || []).map(course => ({
        ...course,
        is_enrolled: false,
        progress: 0,
    }));

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [difficulty, setDifficulty] = useState('All');
    const [sortBy, setSortBy] = useState('title');
    const [sortOrder, setSortOrder] = useState('asc');

    // Temporary states for dropdowns
    const [tempDifficulty, setTempDifficulty] = useState('All');
    const [tempSortBy, setTempSortBy] = useState('title');
    const [tempSortOrder, setTempSortOrder] = useState('asc');

    const [showFilters, setShowFilters] = useState(false);
    const [showSort, setShowSort] = useState(false);

    useEffect(() => {
        document.title = 'Explore Our Courses | Zindua LMS';
    }, []);

    const handleReset = () => {
        setSearchTerm('');
        setDifficulty('All');
        setSortBy('title');
        setSortOrder('asc');
        setShowFilters(false);
        setShowSort(false);
    };

    const handleApplyFilters = () => {
        setDifficulty(tempDifficulty);
        setShowFilters(false);
    };

    const handleApplySort = () => {
        setSortBy(tempSortBy);
        setSortOrder(tempSortOrder);
        setShowSort(false);
    };

    // Filter and sort courses
    let filteredCourses = courses;

    // Filter to show only free courses (price = 0)
    filteredCourses = filteredCourses.filter(course => course.price === 0);

    if (searchTerm) {
        filteredCourses = filteredCourses.filter(
            course =>
                course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (course.summary && course.summary.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }

    if (difficulty !== 'All') {
        filteredCourses = filteredCourses.filter(course => course.difficulty === parseInt(difficulty, 10));
    }

    // Apply sorting
    filteredCourses.sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'title') {
            comparison = a.title.localeCompare(b.title);
        } else if (sortBy === 'rating') {
            comparison = (b.average_rating || 0) - (a.average_rating || 0);
        } else if (sortBy === 'difficulty') {
            comparison = (a.difficulty || 0) - (b.difficulty || 0);
        }
        return sortOrder === 'asc' ? comparison : -comparison;
    });

    return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
            <Header />
            <main className="max-w-350 mx-auto grow mt-20 px-4 sm:px-6 lg:px-8 w-full">
                <div className="flex flex-col gap-2 py-5">
                    <h1 className="">Available Courses</h1>
                    <p className="text-neutral-700">Discover and enroll in our open courses to upskill in tech.</p>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-row gap-3 items-stretch sm:items-center mb-6">
                    <div className="flex-1 relative">
                        <Input
                            id="search"
                            type="text"
                            placeholder="Search courses"
                            className="w-full md:w-1/2 pl-10 pr-4 py-2 border rounded-lg focus:outline-hidden"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    </div>

                    <div className="relative group">
                        <Button
                            className="w-full sm:w-32 flex items-center justify-center gap-6 px-4 py-2 border rounded-lg shadow-none group-hover:border-accent group-hover:text-accent transition-colors"
                            onClick={() => {
                                setShowFilters(!showFilters);
                                setShowSort(false);
                            }}
                        >
                            <span className="hidden sm:inline text-neutral-500 group-hover:text-accent">Filter by</span>
                            <Filter className="w-4 h-4 text-neutral-500 group-hover:text-accent" />
                        </Button>
                        {showFilters && (
                            <div className="absolute right-0 mt-2 w-80 bg-neutral-50 border border-neutral-300 rounded-lg shadow-lg p-4 z-10">
                                <div>
                                    <label htmlFor="difficulty" className="block text-sm text-neutral-950 font-medium mb-2">
                                        Difficulty
                                    </label>
                                    <Select value={tempDifficulty} onValueChange={setTempDifficulty}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select Difficulty" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All</SelectItem>
                                            <SelectItem value="1">Beginner</SelectItem>
                                            <SelectItem value="2">Intermediate</SelectItem>
                                            <SelectItem value="3">Advanced</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col justify-between gap-2 mt-4">
                                    <Button onClick={handleApplyFilters} className="w-full" variant="destructive">
                                        Apply
                                    </Button>
                                    <Button variant="outline" onClick={handleReset} className="w-full">
                                        Reset
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="relative group">
                        <Button
                            className="w-full sm:w-32 flex items-center justify-center gap-6 px-4 py-2 border rounded-lg shadow-none group-hover:border-accent group-hover:text-accent transition-colors"
                            onClick={() => {
                                setShowSort(!showSort);
                                setShowFilters(false);
                            }}
                        >
                            <span className="hidden sm:inline text-neutral-500 group-hover:text-accent">Sort by</span>
                            <SlidersHorizontal className="w-4 h-4 text-neutral-500 group-hover:text-accent" />
                        </Button>
                        {showSort && (
                            <div className="absolute right-0 mt-2 w-80 bg-neutral-50 border border-neutral-300 rounded-lg shadow-lg z-10 p-4">
                                <div className="mb-2">
                                    <label htmlFor="sortBy" className="block text-sm text-neutral-950 font-medium mb-1">
                                        Sort By
                                    </label>
                                    <Select value={tempSortBy} onValueChange={setTempSortBy}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Sort By" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="title">Title</SelectItem>
                                            <SelectItem value="rating">Rating</SelectItem>
                                            <SelectItem value="difficulty">Difficulty</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label htmlFor="sortOrder" className="block text-sm text-neutral-950 font-medium mb-2">
                                        Order
                                    </label>
                                    <Select value={tempSortOrder} onValueChange={setTempSortOrder}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Sort Order" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="asc">Ascending</SelectItem>
                                            <SelectItem value="desc">Descending</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col justify-between gap-2 mt-4">
                                    <Button onClick={handleApplySort} className="w-full" variant="destructive">
                                        Apply
                                    </Button>
                                    <Button variant="outline" onClick={handleReset} className="w-full">
                                        Reset
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Course Grid */}
                <div className="py-6">
                    {isLoading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[...Array(8)].map((_, i) => (
                                <CourseSkeleton key={i} />
                            ))}
                        </div>
                    )}

                    {isError && <DataNotFoundSection title="Could not load courses. Please try again later." />}

                    {!isLoading && !isError && filteredCourses.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredCourses.map(course => (
                                <Link
                                    key={course.id}
                                    to="/cover-course/$courseSlug"
                                    params={{ courseSlug: course.slug }}
                                    search={{}}
                                >
                                    <CourseCard
                                        course={{
                                            ...course,
                                            price: course.price || 'FREE',
                                            thumbnail: course.thumbnail || Thumbnail,
                                        }}
                                    />
                                </Link>
                            ))}
                        </div>
                    )}

                    {!isLoading && !isError && filteredCourses.length === 0 && (
                        <DataNotFoundSection
                            title="No Courses Found."
                            isFiltered={difficulty !== 'All' || !!searchTerm}
                            onResetFilters={handleReset}
                        />
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default CoursesCoverPage;
